import TrackingWallet from '@aon-coverwallet/tracking-wallet';
import { captureException } from 'src/utils/datadogErrorHandling';
import meanBy from 'lodash/meanBy';
import uniq from 'lodash/uniq';
import { appModeName } from './capabilities.js';
import { UrlHelper } from './urlHelper.js';

const APP = 'AgentDashboard';

const tracker = new TrackingWallet({
  commonProps: { app: APP, applicationMode: appModeName },
});

if (window.analytics) {
  tracker.addCommonProps({
    app: APP,
    applicationMode: appModeName,
  });
}

const getAveragePremium = items => {
  if (items.length === 0) return null;
  return meanBy(items, i => i.premium) / 100.0;
};

const timerPromise = time => new Promise(resolve => setTimeout(resolve, time));

const getRequestedLOBs = (business = {}) => {
  const quotes = business.quotes || [];
  return uniq(quotes.map(q => q.insurance_type));
};

const getQuotedLOBs = (business = {}) => {
  const quotes = business.quotes || [];
  return uniq(quotes.filter(q => q.premium > 0).map(q => q.insurance_type));
};

export default class TrackingHelper {
  static track(eventName, eventProps = {}, business) {
    let trackingEventProps = eventProps;
    if (window && window.analytics) {
      const defaultProps = TrackingHelper.getDefaultEventProps();
      const businessProps = business
        ? TrackingHelper.getBusinessProps(business)
        : {};
      const { to: link } = eventProps;
      if (link || link === '') {
        trackingEventProps = {
          ...eventProps,
          to: UrlHelper.getAbsoluteUrl(link),
        };
      }
      tracker.track(eventName, {
        ...defaultProps,
        ...trackingEventProps,
        ...businessProps,
      });
    } else {
      captureException('Tracker failed loading');
    }
    return timerPromise(300);
  }

  static async alias(userId) {
    if (window && window.analytics) {
      await tracker.alias(`${userId}`.toLowerCase());
    }
  }

  static async identify(userId, traits = {}, options = {}) {
    if (window && window.analytics) {
      await tracker.identify(userId, traits, options);
      tracker.addCommonProps(traits);
    }
  }

  static getDefaultEventProps() {
    const pathname = (window && window.location.pathname) || '';
    return {
      app: APP,
      // eslint-disable-next-line no-useless-escape
      page: pathname.replace(/[\/_-]/g, ' ').trim(),
    };
  }

  static getBusinessProps(business) {
    const locations = business.locations || [];
    const quotes = business.quotes || [];
    const policies = business.policies || [];
    const businessLocations = locations.map(location => {
      const { city, state } = location || {};
      return [city, state].filter(e => e).join(', ');
    });

    return {
      annualRevenue: Number(business.annual_revenue),
      businessType: business.business_type,
      subClassCode: business.class_code_ref,
      subClassCodeDescription: business.sub_class_code_description,
      quoteLOBs: getRequestedLOBs(business),
      averageQuotePremium: getAveragePremium(quotes),
      policyLOBs: policies.map(p => p.insurance_type),
      averagePolicyPremium: getAveragePremium(policies),
      businessLocationPrimary: businessLocations[0],
      businessLocations,
      yearsExperience: business.years_experience,
      yearBusinessStarted: business.years_business_started,
      customerEmail: business.contact_email,
      customerBusinessName: business.name,
      subindustry: business.subindustry,
    };
  }
}

export { getRequestedLOBs, getQuotedLOBs, tracker };
